<template>
  <a-row
    type="flex"
    justify="center"
    align="middle"
    style="min-height: 100vh"
    class="ml-3 mr-3"
  >
    <a-spin v-if="state.loading" size="large" />
    <a-col v-if="!state.loading" :sm="20" :md="14" :lg="10">
      <div style="margin-top: 16px">
        <a-steps direction="horizontal" :current="1">
          <a-step
            v-for="(step, stepIndex) in steps"
            :key="stepIndex"
            :title="step.title"
          />
        </a-steps>
      </div>
      <a-typography-title :level="4" style="margin-top: 16px">
        Carga tus documentos
      </a-typography-title>
      <a-typography-paragraph>
        Descarga los formularios, rellénalos y cárgalos.
      </a-typography-paragraph>
      <a-card
        style="margin-top: 16px"
        title="Asignación de coberturas por carga"
      >
        <a-alert
          type="info"
          show-icon
          message="Debes seleccionar al menos una cobertura por cada carga."
          class="mb-3"
        />
        <a-spin :spinning="state.loadingCoverageMatrix">
          <a-empty
            v-if="state.coverageGroups.length === 0"
            description="No hay coberturas disponibles para asignar."
          />
          <a-card
            v-for="group in state.coverageGroups"
            :key="group.key"
            size="small"
            style="margin-bottom: 12px"
          >
            <template #title>
              Póliza #{{ group.policyNumber }} · Grupo {{ group.groupName }}
            </template>
            <a-row
              v-for="coverage in group.coverages"
              :key="coverage.id"
              type="flex"
              align="middle"
              :gutter="8"
              style="padding: 8px 0; border-bottom: 1px solid #f0f0f0"
            >
              <a-col :xs="24" :md="8">
                <a-typography-text>
                  {{ coverage.name }}
                  <a-tag v-if="coverage.dependentRequired" color="blue">
                    Requerida
                  </a-tag>
                </a-typography-text>
              </a-col>
              <a-col
                v-for="dependent in state.dependents"
                :key="dependent.id"
                :xs="12"
                :md="
                  Math.max(
                    4,
                    Math.floor(16 / Math.max(state.dependents.length, 1))
                  )
                "
              >
                <a-checkbox
                  :checked="isCoverageSelected(dependent.id, coverage.id)"
                  :disabled="
                    state.loadingCoverageMatrix || coverage.dependentRequired
                  "
                  @change="
                    (event) =>
                      toggleCoverage(
                        dependent.id,
                        coverage.id,
                        event.target.checked
                      )
                  "
                >
                  {{ getDependentLabel(dependent) }}
                </a-checkbox>
              </a-col>
            </a-row>
          </a-card>
        </a-spin>
      </a-card>
      <a-form layout="vertical" ref="formRef" :model="state">
        <div v-for="insurance in state.form.insurances" :key="insurance.id">
          <a-card
            :title="insurance.businessName"
            style="margin-top: 16px"
            v-for="file in insurance.files"
            :key="file.id"
          >
            <template #extra>
              <ExclamationCircleOutlined
                v-if="!state.fileLists[file.id]"
                style="color: red"
              />
              <CheckCircleOutlined
                v-if="state.fileLists[file.id]"
                style="color: green"
              />
            </template>
            <a-row justify="center">
              <a-typography-text>{{ file.name }}</a-typography-text>
            </a-row>
            <a-row justify="center" style="margin-top: 8px">
              <a-col :span="16">
                <a-upload
                  list-type="text"
                  :multiple="false"
                  :customRequest="
                    ({ file: uploadedFile }) => handleUpload(uploadedFile, file)
                  "
                  :file-list="state.fileLists[file.id]"
                  accept=".pdf, .doc, .docx, .xls, .xlsx"
                  :remove="(removedFile) => handleUploadRemove(file)"
                >
                  <a-button
                    :loading="state.fileLists[file.id]?.[0]?.loading"
                    type="primary"
                    block
                  >
                    Subir documento
                  </a-button>
                </a-upload>
              </a-col>
            </a-row>
            <a-row justify="center" style="margin-top: 8px">
              <a-typography-text type="secondary">
                Puedes descargar el documento a completar en el siguiente link
              </a-typography-text>
            </a-row>
            <a-row justify="center">
              <a-button type="link" size="small" :href="file.url">
                Descargar documento
              </a-button>
            </a-row>
          </a-card>
          <a-card style="margin-top: 8px">
            <a-typography-title class="text-gray-8" :level="5">
              Sube tu cédula de identidad
            </a-typography-title>
            <a-typography-text class="text-gray-8">
              Por favor, sube imágenes de ambos lados de tu cédula de identidad.
              Haz clic en “Cédula Anverso” para subir la parte frontal y en
              “Cédula Reverso” para subir la parte trasera. Se aceptan archivos
              en formato JPG, JPEG o PNG.
            </a-typography-text>
            <a-row style="margin-top: 16px" :gutter="[8, 8]">
              <a-col :span="12">
                <a-upload
                  name="idFront"
                  :file-list="state.idFront"
                  :customRequest="
                    ({ file, filename }) =>
                      onUpload({ file, filename, side: 'front' })
                  "
                  accept=".jpg,.jpeg,.png"
                  :disabled="state.idFront[0]?.loading"
                  :show-upload-list="false"
                >
                  <a-card
                    :style="{
                      textAlign: 'center',
                      cursor: state.idFront[0]?.loading
                        ? 'not-allowed'
                        : 'pointer'
                    }"
                  >
                    <img
                      v-if="!state.idFront[0]"
                      src="@/assets/images/insured_form/anverso.svg"
                    />
                    <div v-if="!state.idFront[0]">Cédula Anverso</div>
                    <img
                      v-else
                      style="object-fit: contain; width: 100%; height: 100%"
                      :src="state.idFront[0]?.preview"
                    />
                  </a-card>
                </a-upload>
              </a-col>
              <a-col :span="12">
                <a-upload
                  name="idBack"
                  :file-list="state.idBack"
                  :customRequest="
                    ({ file, filename }) =>
                      onUpload({ file, filename, side: 'back' })
                  "
                  accept=".jpg,.jpeg,.png"
                  :disabled="state.idBack[0]?.loading"
                  :show-upload-list="false"
                >
                  <a-card
                    :style="{
                      textAlign: 'center',
                      cursor: state.idBack[0]?.loading
                        ? 'not-allowed'
                        : 'pointer'
                    }"
                  >
                    <img
                      v-if="!state.idBack[0]"
                      src="@/assets/images/insured_form/reverso.svg"
                    />
                    <div v-if="!state.idBack[0]">Cédula Reverso</div>
                    <img
                      v-else
                      style="object-fit: contain; width: 100%; height: 100%"
                      :src="state.idBack[0]?.preview"
                    />
                  </a-card>
                </a-upload>
              </a-col>
            </a-row>
          </a-card>
        </div>
      </a-form>
      <a-row style="margin-top: 8px" :gutter="16">
        <a-col :span="24">
          <a-card>
            <a-typography-title class="text-gray-8" :level="5">
              Declaración de autorización personal
            </a-typography-title>
            <div class="d-flex align-items-start">
              <a-checkbox
                v-model:checked="checked"
                @click="toggleChecked"
                class="d-flex mr-2"
                :disabled="submittingConsent"
              >
                <a-typography-text class="text-gray-8">
                  {{ consent }}
                </a-typography-text>
              </a-checkbox>
            </div>
          </a-card>
          <br />
        </a-col>
      </a-row>
      <a-row
        style="margin-top: 18px; margin-bottom: 18px"
        :gutter="16"
        type="flex"
        justify="space-between"
      >
        <a-col :span="8">
          <a-button
            :loading="
              Object.values(state.fileLists).some((fl) =>
                fl.some((fl) => fl.loading)
              )
            "
            block
            size="large"
          >
            <router-link
              :loading="
                state.loadingSendToBroker ||
                Object.values(state.fileLists).some((fl) =>
                  fl.some((fl) => fl.loading)
                )
              "
              :to="{
                name: 'DependentsIncorporationFormStep1',
                params: { token: route.params.token }
              }"
            >
              Atrás
            </router-link>
          </a-button>
        </a-col>
        <a-col :span="8">
          <a-button
            :loading="
              state.loadingSendToBroker ||
              Object.values(state.fileLists).some((fl) =>
                fl.some((fl) => fl.loading)
              ) ||
              submittingConsent ||
              state.idFront[0]?.loading ||
              state.idBack[0]?.loading
            "
            :disabled="
              !state.form.insurances.every((i) =>
                i.files.every((f) => state.fileLists[f.id])
              ) ||
              !checked ||
              submittingConsent ||
              !state.idFront[0] ||
              !state.idBack[0]
            "
            block
            type="primary"
            size="large"
            @click="submitForm"
          >
            Finalizar
          </a-button>
        </a-col>
      </a-row>
    </a-col>
  </a-row>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { reactive, ref, onMounted } from 'vue';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons-vue';
import {
  FETCH_DEPENDENTS_INCORPORATION_FORM,
  FETCH_TICKET_DEPENDENT_INCORPORATIONS,
  FETCH_TICKET_AVAILABLE_COVERAGES,
  FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
  PATCH_TICKET_STATUS,
  PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
  PUT_DOCUMENT_OF_DEPENDENTS_INCORPORATION,
  POST_CARNET,
  FETCH_CONSENT_TEXT,
  SAVE_CONSENT_DATA
} from '@/store/types';
import notification from '@/utils/notifications';
import { getTicketV2 } from '@/network';

const route = useRoute();
const router = useRouter();
const store = useStore();

const steps = [
  {
    title: 'Ingresa tus cargas'
  },
  {
    title: 'Carga tus documentos'
  }
];

const formRef = ref();

const state = reactive({
  loading: true,
  form: {},
  ticket: {},
  dependents: [],
  coverageGroups: [],
  coverageSelections: {},
  loadingCoverageMatrix: false,
  fileLists: {},
  loadingSendToBroker: false,
  idFront: [],
  idBack: []
});

const ticketId = ref();
const consent = ref('');
const checked = ref(false);

const getArrayLength = (value) => (Array.isArray(value) ? value.length : 0);

const getCoverageId = (coverage) => {
  const id =
    coverage?.employeeCoverageId ?? coverage?.operationalId ?? coverage?.id;
  const numericId = Number(id);
  return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
};

const hasAllowDependentsFlag = (coverage) => {
  const flag = [
    coverage?.allowDependents,
    coverage?.coverage?.allowDependents
  ].find((value) => value !== undefined && value !== null);

  // Endpoint already applies allow_dependents=true. Keep unannotated response
  // rows because older v2 responses do not expose the flag.
  return flag === undefined || flag === true || flag === 1 || flag === 'true';
};

const getDependentLabel = (dependent) =>
  [
    dependent?.firstName,
    dependent?.secondName,
    dependent?.firstSurname,
    dependent?.secondSurname
  ]
    .filter(Boolean)
    .join(' ') ||
  dependent?.fullname ||
  dependent?.rut ||
  'Carga';

const buildCoverageGroups = (coverages) => {
  const groups = new Map();

  for (const coverage of coverages) {
    if (!hasAllowDependentsFlag(coverage)) continue;

    const id = getCoverageId(coverage);
    if (!id) continue;

    const policy = coverage?.policy || {};
    const groupCompany = coverage?.groupCompany || {};
    const policyId = policy.id ?? policy.numberPolicy ?? '-';
    const groupId = groupCompany.id ?? groupCompany.name ?? '-';
    const key = `${policyId}-${groupId}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        policyNumber: policy.numberPolicy || policy.id || '-',
        groupName: groupCompany.name || groupCompany.id || '-',
        coverages: []
      });
    }

    groups.get(key).coverages.push({
      id,
      name: coverage?.coverage?.name || 'Cobertura',
      dependentRequired: Boolean(coverage?.coverage?.dependentRequired)
    });
  }

  return Array.from(groups.values());
};

const isCoverageSelected = (dependentId, coverageId) =>
  (state.coverageSelections[dependentId] || []).includes(coverageId);

const toggleCoverage = (dependentId, coverageId, checked) => {
  const current = state.coverageSelections[dependentId] || [];
  state.coverageSelections[dependentId] = checked
    ? current.includes(coverageId)
      ? current
      : [...current, coverageId]
    : current.filter((id) => id !== coverageId);
};

const loadCoverageMatrix = async () => {
  state.loadingCoverageMatrix = true;
  try {
    const [dependents, availableCoverages] = await Promise.all([
      store.dispatch(FETCH_TICKET_DEPENDENT_INCORPORATIONS, {
        ticketId: ticketId.value,
        token: route.params.token
      }),
      store.dispatch(FETCH_TICKET_AVAILABLE_COVERAGES, {
        ticketId: ticketId.value,
        token: route.params.token
      })
    ]);

    state.dependents = Array.isArray(dependents) ? dependents : [];
    state.coverageGroups = buildCoverageGroups(
      Array.isArray(availableCoverages) ? availableCoverages : []
    );
    const availableCoverageIds = new Set(
      state.coverageGroups.flatMap((group) =>
        group.coverages.map((coverage) => coverage.id)
      )
    );

    const requiredCoverageIds = new Set(
      state.coverageGroups.flatMap((group) =>
        group.coverages
          .filter((coverage) => coverage.dependentRequired)
          .map((coverage) => coverage.id)
      )
    );

    const selectionEntries = await Promise.all(
      state.dependents.map(async (dependent) => {
        const response = await store.dispatch(
          FETCH_TICKET_DEPENDENT_INCORPORATION_COVERAGES,
          {
            ticketId: ticketId.value,
            dependentIncorporationId: dependent.id,
            token: route.params.token
          }
        );
        const persistedCoverageIds =
          response?.employeeCoverageIncorporationIds ??
          response?.employeeCoverageIds;
        const selectedCoverageIds = Array.isArray(persistedCoverageIds)
          ? persistedCoverageIds
              .map(Number)
              .filter((id) => availableCoverageIds.has(id))
          : [];

        return [
          dependent.id,
          [...new Set([...selectedCoverageIds, ...requiredCoverageIds])]
        ];
      })
    );

    state.coverageSelections = Object.fromEntries(selectionEntries);
  } finally {
    state.loadingCoverageMatrix = false;
  }
};

const persistCoverageMatrix = async () => {
  for (const dependent of state.dependents) {
    const selectedCoverageIds = state.coverageSelections[dependent.id] || [];
    await store.dispatch(PUT_TICKET_DEPENDENT_INCORPORATION_COVERAGES, {
      ticketId: ticketId.value,
      dependentIncorporationId: dependent.id,
      token: route.params.token,
      payload: {
        coverageIds: selectedCoverageIds
      }
    });
  }
};

const validateDependentsBeforeSendToBroker = async () => {
  if (!Array.isArray(state.dependents) || state.dependents.length === 0) {
    notification('error', 'Error', 'Debe incorporar al menos una carga.');
    return false;
  }

  const missingSelections = state.dependents.some(
    (dependent) =>
      getArrayLength(state.coverageSelections[dependent.id] || []) === 0
  );
  if (missingSelections) {
    notification(
      'error',
      'Error',
      'Todas las cargas deben tener al menos una cobertura seleccionada.'
    );
    return false;
  }

  return true;
};

const submitForm = async () => {
  state.loadingSendToBroker = true;
  const isValid = await formRef.value.validate();
  if (!isValid) {
    state.loadingSendToBroker = false;
    return;
  }
  if (
    !state.form.insurances.every((i) =>
      i.files.every((f) => state.fileLists[f.id])
    )
  ) {
    notification('error', 'Error', 'Debes subir todos los documentos');
    state.loadingSendToBroker = false;
    return;
  }
  const isDependentsCoverageValid =
    await validateDependentsBeforeSendToBroker();
  if (!isDependentsCoverageValid) {
    state.loadingSendToBroker = false;
    return;
  }
  try {
    await persistCoverageMatrix();
    await store.dispatch(PATCH_TICKET_STATUS, {
      ticketId: ticketId.value,
      token: route.params.token,
      status: 7
    });
    router.push({
      name: 'DependentsIncorporationFormSuccess',
      params: { token: route.params.token }
    });
  } catch (error) {
    const status = error?.response?.status;
    const backendMessage = error?.response?.data?.message;
    if (
      (status === 400 || status === 401 || status === 409) &&
      backendMessage
    ) {
      notification('error', 'Error', backendMessage);
      state.loadingSendToBroker = false;
      return;
    }
    notification(
      'error',
      'Error',
      'Ocurrió un error al finalizar el formulario de incorporación de cargas. Intentalo más tarde.'
    );
  }
  state.loadingSendToBroker = false;
};

const handleUpload = async (uploadedFile, insuranceFile) => {
  const currentFile = state.fileLists[insuranceFile.id];
  const fileSize = uploadedFile.size / 1024 / 1024;
  if (fileSize > 5) {
    state.fileLists[insuranceFile.id] = currentFile;
    notification(
      'error',
      'Error',
      'El archivo del documento excede el peso máximo 5MB.'
    );
    return;
  } else if (
    uploadedFile.type != 'image/png' &&
    uploadedFile.type != 'image/jpg' &&
    uploadedFile.type != 'application/pdf'
  ) {
    state.fileLists[insuranceFile.id] = currentFile;
    notification('error', 'Error', 'Sólo se permiten archivos pdf e imágenes.');
    return;
  }
  state.fileLists[insuranceFile.id] = currentFile
    ? currentFile.map((f) => ({ ...f, loading: true }))
    : [{ ...uploadedFile, name: uploadedFile.name, loading: true }];
  const formData = new FormData();
  formData.append('file', uploadedFile);
  formData.append('fileInsuranceCompanyId', insuranceFile.id);
  try {
    await store.dispatch(PUT_DOCUMENT_OF_DEPENDENTS_INCORPORATION, {
      token: route.params.token,
      payload: formData
    });
    state.fileLists[insuranceFile.id] = [uploadedFile];
    notification('success', 'Carga exitosa', 'Documento cargado con éxito');
  } catch (error) {
    state.fileLists[insuranceFile.id] = currentFile;
    if (error.response.status === 400 && error?.response?.data?.message) {
      notification('error', 'Error', error.response.data.message);
    } else {
      notification(
        'error',
        'Error',
        'Ocurrió un error al subir el documento. Intentalo más tarde.'
      );
    }
  }
};

const handleUploadRemove = (insuranceFile) => {
  delete state.fileLists[insuranceFile.id];
};

const uploadCarnet = async ({ file, name, ticketId }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ticketId', ticketId);
  formData.append('name', name);
  await store.dispatch(POST_CARNET, {
    payload: formData,
    token: route.params.token
  });
};

const onUpload = async ({ file, side }) => {
  const fileSize = file.size / 1024 / 1024;
  if (fileSize > 5) {
    notification('error', 'Error', 'El archivo excede el peso máximo de 5MB.');
    return;
  }

  if (
    file.type !== 'image/png' &&
    file.type !== 'image/jpeg' &&
    file.type !== 'image/jpg'
  ) {
    notification(
      'error',
      'Error',
      'Sólo se permiten archivos JPG, JPEG o PNG.'
    );
    return;
  }

  const isFront = side === 'front';
  const name = isFront ? 'Anverso' : 'Reverso';
  const listKey = isFront ? 'idFront' : 'idBack';

  state[listKey] = [{ ...file, name, loading: true }];

  try {
    await uploadCarnet({ file, name, ticketId: ticketId.value });
    await handlePreview(file);
    state[listKey] = [{ ...file, name, loading: false }];
    notification('success', 'Carga exitosa', 'Imagen cargada con éxito');
  } catch (error) {
    console.log(error);
    state[listKey] = [];
    notification('error', 'Error', 'Ocurrió un error al subir la imagen.');
  }
};

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

const handlePreview = async (file) => {
  if (!file.preview) {
    file.preview = await getBase64(file);
  }
};

const submittingConsent = ref(false);
const toggleChecked = async () => {
  submittingConsent.value = true;
  checked.value = !checked.value;
  if (!checked.value) {
    submittingConsent.value = false;
    return;
  }
  try {
    await store.dispatch(SAVE_CONSENT_DATA, {
      id: ticketId.value,
      token: route.params.token
    });
  } catch (error) {
    notification(
      'error',
      'Error',
      'Ocurrió un error al guardar tu consentimiento. Intentalo más tarde.'
    );
    checked.value = !checked.value;
  }
  submittingConsent.value = false;
};

onMounted(async () => {
  try {
    const token = route.params.token;
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    ticketId.value = decodedToken.tid;
    const ticketResponse = await getTicketV2(ticketId.value, token);
    state.ticket = ticketResponse.data;

    const form = await store.dispatch(
      FETCH_DEPENDENTS_INCORPORATION_FORM,
      route.params.token
    );
    state.form = form;

    for (const insurance of form.insurances) {
      for (const file of insurance.files) {
        if (file.uploadedFile) {
          state.fileLists[file.id] = [
            {
              uid: String(file.uploadedFile.id),
              name: file.uploadedFile.name
            }
          ];
        }
      }
    }

    if (form.state !== 'Creado') {
      router.push({ name: 'DependentsIncorporationFormError' });
    } else if (form.isTokenExpired) {
      router.push({ name: 'DependentsIncorporationFormExpired' });
    }
    await loadCoverageMatrix();

    const consentResponse = await store.dispatch(
      FETCH_CONSENT_TEXT,
      { id: ticketId.value, token: route.params.token }
    );
    consent.value = consentResponse.data;

    state.loading = false;
  } catch (error) {
    console.log(error);
    router.push({ name: 'DependentsIncorporationFormError' });
  }
});
</script>
<style>
.ant-upload {
  width: 100%;
}
</style>
